/***
**Module Name: StripeModeCard
 **File Name :  StripeModeCard.jsx
 **Project :    SPOT SAVER
 **Copyright(c): SPOT SAVER.
 **Organization: SPOT SAVER
 **author :     Hari
 **license :
 **version :    1.0.0
 **Created on : Sep 03 2026
 **Last modified on:
 **Description: The Settings page's "Stripe Mode" card — shows which Stripe environment
 **             this tenant is pointed at and lets an admin switch it.
 **
 **             Both calls behind this card are authenticated server-side
 **             (controllers/requireAdminSession.js in myspotsaver-api). The card is a
 **             convenience, NOT the security boundary: hiding the button would not stop
 **             anyone, and the API rejects an unauthenticated POST regardless.
 **
 **             Switching to Live requires typing the word LIVE. That deliberate friction
 **             is not decoration — from the moment it lands, every booking charges a real
 **             card, and every venue that has not onboarded in live mode falls back to a
 **             plain platform charge that has to be settled by hand. Switching back to
 **             Sandbox only asks for a plain confirmation, because it stops real money
 **             rather than starting it.
 ***/
import React, { useState, useEffect, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import tmdbApi from "../../api/tmdbApi";

const SANDBOX = "sandbox";
const PRODUCTION = "production";

// Pulled from spot-admin.css's :root so the card sits in the same palette as the rest
// of the admin rather than importing Bootstrap's default greens and reds.
const PURPLE = "#5D1CD8";
const HEADING = "#4F378A";
const MUTED = "#5C6188";
const BORDER = "#E2E8F0";
const PANEL = "#FAFBFE";
const DANGER = "#DC5354";
const LIVE_GREEN = "#0F9D58";

const StripeModeCard = ({ onSessionExpired }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Surfaced inline rather than swallowed: the most important thing this card can say
    // is "your switch to Live was refused because there is no live key", and a silent
    // failure would leave the admin believing the platform had gone live.
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [confirmSandbox, setConfirmSandbox] = useState(false);
    const [confirmLive, setConfirmLive] = useState(false);
    const [typedConfirm, setTypedConfirm] = useState("");

    // The API returns the session-expired string as a RESULT, not an HTTP error, which is
    // the shape every other page here matches on.
    const isSessionExpired = (payload) => payload === "Invalid token or Expired"
        || payload?.result === "Invalid token or Expired";

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await tmdbApi.getStripeMode();
            if (isSessionExpired(response)) {
                if (onSessionExpired) onSessionExpired();
                return;
            }
            const result = response?.result;
            if (result?.error) {
                setError(result.message || "Could not read the Stripe mode.");
                return;
            }
            setData(result || null);
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Could not reach the API.");
        } finally {
            setLoading(false);
        }
    }, [onSessionExpired]);

    useEffect(() => { load(); }, [load]);

    const applyMode = async (mode) => {
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const response = await tmdbApi.setStripeMode(mode);
            if (isSessionExpired(response)) {
                if (onSessionExpired) onSessionExpired();
                return;
            }
            const result = response?.result;
            if (result?.error) {
                // e.g. "STRIPE_SECRET_KEY_LIVE is not set on this environment…". The
                // refusal carries the current stats too, so the card still re-renders
                // with the truth rather than going blank.
                setError(result.message || "The switch was refused.");
                if (result.mode) setData(result);
                return;
            }
            setData(result || null);
            setSuccess(result?.unchanged
                ? `Already in ${mode === PRODUCTION ? "Live" : "Sandbox"} mode.`
                : `Switched to ${mode === PRODUCTION ? "LIVE" : "SANDBOX"} mode.`);
            // Re-read rather than trust the POST's own payload — the card should show
            // what the API says now, and this is also what proves the write stuck.
            await load();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "The switch failed.");
        } finally {
            setSaving(false);
        }
    };

    const isLive = data?.mode === PRODUCTION;
    const stats = data?.stats || {};

    // The API's `missingForCurrentTarget` is computed for the mode you are currently IN.
    // The switch-to-Live dialog needs the shortfall for the mode you are switching TO,
    // and while you are in sandbox those are different numbers — quoting the sandbox
    // figure under the words "have no live Stripe account" would understate the blast
    // radius of the switch, usually as a reassuring zero. Derived from the live count,
    // which the same payload already carries.
    const missingForLive = Math.max((stats.approvedVenues ?? 0) - (stats.haveLiveAccount ?? 0), 0);
    const hasShortfall = missingForLive > 0;

    const startSwitch = () => {
        setError("");
        setSuccess("");
        setTypedConfirm("");
        if (isLive) {
            setConfirmSandbox(true);
        } else {
            setConfirmLive(true);
        }
    };

    const closeLive = () => { setConfirmLive(false); setTypedConfirm(""); };

    // The four numbers across the top of the card. The shortfall tile turns amber once
    // there is one, so the thing an admin has to act on is the thing that catches the eye.
    const Stat = ({ label, value, warn, id }) => (
        <div className="col-6 col-lg-3 mb-2">
            <div
                className="h-100 px-3 py-2 rounded-3"
                id={id}
                style={{
                    background: warn ? "rgba(251, 139, 69, 0.08)" : PANEL,
                    border: `1px solid ${warn ? "rgba(251, 139, 69, 0.45)" : BORDER}`,
                }}
            >
                <div style={{ color: MUTED, fontSize: 12, fontWeight: 600 }}>{label}</div>
                <div style={{ color: warn ? "#C25D18" : HEADING, fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>
                    {value}
                </div>
            </div>
        </div>
    );

    return (
        <div className="card">
            <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <h4 className="card-title mb-0">STRIPE MODE</h4>
                    {data && (
                        <span
                            className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                            id="stripe-mode-badge"
                            style={{
                                fontSize: 12,
                                fontWeight: 700,
                                letterSpacing: "0.5px",
                                color: isLive ? LIVE_GREEN : MUTED,
                                background: isLive ? "rgba(15, 157, 88, 0.12)" : "rgba(92, 97, 136, 0.10)",
                                border: `1px solid ${isLive ? "rgba(15, 157, 88, 0.35)" : BORDER}`,
                            }}
                        >
                            <span
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    background: isLive ? LIVE_GREEN : MUTED,
                                    display: "inline-block",
                                }}
                            />
                            {isLive ? "LIVE" : "SANDBOX"}
                        </span>
                    )}
                </div>

                <p style={{ color: MUTED, fontSize: 14 }} className="mb-3">
                    Controls which Stripe environment is used for all payments, connected
                    accounts, and transfers.
                </p>

                {loading && <p className="mb-0">Loading…</p>}

                {!loading && error && (
                    <div className="alert alert-danger" role="alert">{error}</div>
                )}
                {!loading && success && !error && (
                    <div className="alert alert-success" role="alert">{success}</div>
                )}

                {!loading && data && (
                    <>
                        {/* A key configured for the CURRENT mode is the difference between
                            a working environment and one where every Stripe call fails. */}
                        {!data.keyConfigured && (
                            <div className="alert alert-warning" role="alert">
                                No Stripe secret key is configured in this environment for{" "}
                                <strong>{isLive ? "LIVE" : "SANDBOX"}</strong> mode. Set{" "}
                                <code>{isLive ? "STRIPE_SECRET_KEY_LIVE" : "STRIPE_SECRET_KEY_TEST"}</code>{" "}
                                before taking payments.
                            </div>
                        )}
                        {/* The publishable key is the half that fails silently: the server
                            looks healthy, and the mismatch only surfaces when a customer
                            tries to pay. Worth its own warning rather than folding into the
                            secret-key one. */}
                        {data.publishableKeyConfigured === false && (
                            <div className="alert alert-warning" role="alert">
                                No Stripe <strong>publishable</strong> key is stored for{" "}
                                <strong>{isLive ? "LIVE" : "SANDBOX"}</strong> mode
                                (<code>common.{isLive ? "stripeLive" : "stripe"}</code> on this
                                appname's config document). The apps cannot confirm a payment
                                without it.
                            </div>
                        )}
                        {data.keyWarning && (
                            <div className="alert alert-warning" role="alert">{data.keyWarning}</div>
                        )}
                        {/* Says why Live is unavailable before the admin clicks and gets
                            refused. Only shown while in sandbox — in live it is moot. */}
                        {!isLive && data.liveBlockedReason && (
                            <div className="alert alert-secondary" role="alert">
                                <strong>Switching to Live is currently blocked:</strong>{" "}
                                {data.liveBlockedReason}
                            </div>
                        )}

                        <div className="row mb-3">
                            <Stat id="stat-approved-venues" label="Approved venues" value={stats.approvedVenues ?? "—"} />
                            <Stat id="stat-sandbox-accounts" label="Have a sandbox account" value={stats.haveSandboxAccount ?? "—"} />
                            <Stat id="stat-live-accounts" label="Have a live account" value={stats.haveLiveAccount ?? "—"} />
                            <Stat
                                id="stat-missing-accounts"
                                label={`Missing an account for ${isLive ? "LIVE" : "SANDBOX"}`}
                                value={stats.missingForCurrentTarget ?? "—"}
                                warn={(stats.missingForCurrentTarget ?? 0) > 0}
                            />
                        </div>

                        <p style={{ color: MUTED, fontSize: 13 }} className="mb-3">
                            A venue only becomes payout-ready in a mode once it has completed
                            Stripe onboarding in that mode. Until it does, its <strong>paid
                            plans are hidden from the mobile app</strong>, and a location with
                            no free plan is hidden entirely — the app will not offer a plan the
                            venue cannot be paid for. Bookings on free plans are unaffected.
                        </p>

                        <button
                            className="fill-btn-style"
                            type="button"
                            id="stripe-mode-switch-btn"
                            style={{ textTransform: "none" }}
                            disabled={saving || (!isLive && !!data.liveBlockedReason)}
                            onClick={startSwitch}
                        >
                            {saving ? "Switching…" : (isLive ? "Switch to Sandbox" : "Switch to Live")}
                        </button>
                    </>
                )}
            </div>

            {/* Switching back to Sandbox stops real money moving, so a plain confirm. */}
            <Modal show={confirmSandbox} centered backdrop="static" onHide={() => setConfirmSandbox(false)}>
                <div className="modal-body text-center px-4 pt-4 pb-2" id="stripe-sandbox-confirm-body">
                    <div
                        className="d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(93, 28, 216, 0.10)" }}
                    >
                        <span className="material-icons" style={{ fontSize: 34, color: PURPLE }}>science</span>
                    </div>
                    <h3 style={{ fontSize: 22, fontWeight: 700, color: HEADING }}>Switch to Sandbox?</h3>
                    <p style={{ color: MUTED, fontSize: 14, marginBottom: 0 }}>
                        All payments, connected accounts and transfers will use Stripe test mode.
                        Live data is kept and comes back exactly as it is if you switch to Live again.
                    </p>
                </div>
                <div className="modal-footer border-0 justify-content-center gap-3 pb-4">
                    <button
                        className="stroke-btn-style"
                        type="button"
                        id="stripe-sandbox-cancel-btn"
                        style={{ textTransform: "none" }}
                        onClick={() => setConfirmSandbox(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="fill-btn-style"
                        type="button"
                        id="stripe-sandbox-confirm-btn"
                        style={{ textTransform: "none" }}
                        onClick={() => { setConfirmSandbox(false); applyMode(SANDBOX); }}
                    >
                        Switch to Sandbox
                    </button>
                </div>
            </Modal>

            {/* Switching to Live STARTS real money moving — typed confirmation. */}
            <Modal show={confirmLive} centered backdrop="static" onHide={closeLive}>
                <div className="modal-body px-4 pt-4 pb-2" id="stripe-live-confirm-body">
                    <div className="text-center mb-3">
                        <div
                            className="d-inline-flex align-items-center justify-content-center mb-3"
                            style={{ width: 64, height: 64, borderRadius: "50%", background: "rgba(220, 83, 84, 0.12)" }}
                        >
                            <span className="material-icons" style={{ fontSize: 34, color: DANGER }}>warning_amber</span>
                        </div>
                        <h3 style={{ fontSize: 22, fontWeight: 700, color: HEADING, marginBottom: 0 }}>
                            Switch to LIVE mode?
                        </h3>
                    </div>

                    <p style={{ color: MUTED, fontSize: 14 }}>
                        Every booking will charge a <strong>real card</strong>. Until each venue
                        re-onboards in live mode its paid plans are hidden from the mobile app, and
                        locations with no free plan disappear from the list.
                    </p>

                    {/* The blast radius as a number, not buried in the paragraph — this is the
                        one figure that decides whether today is the day to flip the switch.
                        Only alarming when there is something to be alarmed about: with every
                        venue already onboarded the same panel reads as reassurance. */}
                    <div
                        className="d-flex align-items-center p-3 rounded-3 mb-3"
                        id="stripe-live-impact"
                        style={{
                            background: hasShortfall ? "rgba(220, 83, 84, 0.07)" : "rgba(15, 157, 88, 0.07)",
                            border: `1px solid ${hasShortfall ? "rgba(220, 83, 84, 0.30)" : "rgba(15, 157, 88, 0.30)"}`,
                        }}
                    >
                        {/* flexShrink:0 — without it the denominator is squeezed onto its own
                            line and the ratio stops reading as one number. */}
                        <div className="text-center pe-3" style={{ flexShrink: 0, minWidth: 74 }}>
                            <div
                                style={{
                                    fontSize: 28,
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    color: hasShortfall ? DANGER : LIVE_GREEN,
                                }}
                            >
                                {missingForLive}
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, whiteSpace: "nowrap" }}>
                                of {stats.approvedVenues ?? 0}
                            </div>
                        </div>
                        <div className="ps-3" style={{ color: MUTED, fontSize: 13, borderLeft: `1px solid ${BORDER}` }}>
                            {hasShortfall ? (
                                <>
                                    approved venues have <strong>no live Stripe account yet</strong>. Their
                                    payouts must be settled by hand until they finish Stripe onboarding in
                                    live mode.
                                </>
                            ) : (
                                <>
                                    approved venues are still missing a live Stripe account — every one of
                                    them is already onboarded and can be paid out normally.
                                </>
                            )}
                        </div>
                    </div>

                    <label
                        htmlFor="stripe-live-confirm-input"
                        className="form-label"
                        style={{ color: MUTED, fontSize: 13, fontWeight: 600 }}
                    >
                        Type <strong style={{ color: HEADING }}>LIVE</strong> to confirm
                    </label>
                    <input
                        id="stripe-live-confirm-input"
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        value={typedConfirm}
                        onChange={(e) => setTypedConfirm(e.target.value)}
                        placeholder="LIVE"
                        style={{
                            borderRadius: 10,
                            border: `1px solid ${BORDER}`,
                            background: PANEL,
                            letterSpacing: "2px",
                            fontWeight: 600,
                        }}
                    />
                </div>
                <div className="modal-footer border-0 justify-content-center gap-3 pb-4">
                    <button
                        className="stroke-btn-style"
                        type="button"
                        id="stripe-live-cancel-btn"
                        style={{ textTransform: "none" }}
                        onClick={closeLive}
                    >
                        Cancel
                    </button>
                    <button
                        className="fill-btn-style"
                        type="button"
                        id="stripe-live-confirm-btn"
                        disabled={typedConfirm !== "LIVE"}
                        style={{
                            textTransform: "none",
                            background: DANGER,
                            borderColor: DANGER,
                            opacity: typedConfirm === "LIVE" ? 1 : 0.45,
                            cursor: typedConfirm === "LIVE" ? "pointer" : "not-allowed",
                        }}
                        onClick={() => {
                            if (typedConfirm !== "LIVE") return;
                            closeLive();
                            applyMode(PRODUCTION);
                        }}
                    >
                        Switch to LIVE
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default StripeModeCard;
